import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import type { AuthConfig } from "../types";

// The shell matches a single path segment, so slashes never resolve.
const URL_PATH_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
const PREPROD_AUTHORITY_URL = "https://sam.preprod.accuris.dev/sso/oauth2/authorize";
const PROD_AUTHORITY_URL = "https://sam.accuristech.com/sso/oauth2/authorize";

function kebabToCamel(str: string): string {
  return str.replace(/-./g, (x) => x[1].toUpperCase());
}

@customElement("register-mfe-dialog")
export class RegisterMfeDialog extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .form-field {
      margin-bottom: var(--spacing-16, 16px);
    }

    .form-field label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-bottom: var(--spacing-4, 4px);
    }

    .form-field .help {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
      margin-top: var(--spacing-4, 4px);
    }

    .derived-fields {
      background: var(--colors-background-bg-gray-50, #f9fafb);
      border: 1px solid var(--colors-border-border-gray-300, #e0e3e8);
      border-radius: 6px;
      padding: var(--spacing-12, 12px);
      margin-bottom: var(--spacing-16, 16px);
    }

    .derived-fields .field-row {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      font-size: 13px;
      margin-bottom: var(--spacing-4, 4px);
    }

    .derived-fields .field-row:last-child {
      margin-bottom: 0;
    }

    .derived-fields .field-label {
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
      min-width: 100px;
    }

    .derived-fields .field-value {
      font-family: monospace;
      font-size: 12px;
      color: var(--colors-foreground-fg-gray-900, #101828);
    }

    .form-field label.checkbox-row {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      cursor: pointer;
    }

    .form-field .field-error {
      font-size: 11px;
      color: var(--colors-foreground-fg-error-600, #d92d20);
      margin-top: var(--spacing-4, 4px);
    }

    .auth-section {
      border-left: 2px solid var(--colors-border-border-gray-300, #e0e3e8);
      padding-left: var(--spacing-12, 12px);
      margin-bottom: var(--spacing-16, 16px);
    }

    .auth-section .section-title {
      font-size: 13px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-900, #101828);
      margin-bottom: var(--spacing-8, 8px);
    }

    .auth-section .section-title span {
      font-weight: 400;
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
      margin-left: var(--spacing-4, 4px);
    }

    .json-preview {
      background: var(--colors-background-bg-gray-50, #f9fafb);
      border: 1px solid var(--colors-border-border-gray-300, #e0e3e8);
      border-radius: 6px;
      padding: var(--spacing-12, 12px);
      margin-bottom: var(--spacing-16, 16px);
    }

    .json-preview summary {
      font-size: 12px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      cursor: pointer;
      user-select: none;
    }

    .json-preview pre {
      margin: var(--spacing-8, 8px) 0 0;
      padding: var(--spacing-8, 8px);
      background: var(--colors-background-bg-gray-900, #101828);
      color: #e0e3e8;
      border-radius: 4px;
      font-family: monospace;
      font-size: 12px;
      line-height: 1.5;
      overflow-x: auto;
      white-space: pre;
    }

    .modal-footer {
      display: flex;
      gap: var(--spacing-8, 8px);
      justify-content: flex-end;
    }

    .success-message {
      text-align: center;
      padding: var(--spacing-16, 16px) 0;
    }

    .success-message a {
      color: var(--colors-foreground-fg-brand-600, #4a90d9);
      text-decoration: none;
      font-weight: 600;
    }

    .success-message a:hover {
      text-decoration: underline;
    }

    bee-alert {
      margin-bottom: var(--spacing-16, 16px);
    }

    bee-alert a {
      color: var(--colors-foreground-fg-brand-600, #4a90d9);
      text-decoration: none;
      font-weight: 600;
    }

    bee-alert a:hover {
      text-decoration: underline;
    }
  `;

  @property({ type: Boolean }) open = false;

  @state() private _mfeName = "";
  @state() private _urlPath = "";
  @state() private _urlPathEdited = false;
  @state() private _applying = false;
  @state() private _error = "";
  @state() private _prUrl = "";
  @state() private _prodPrUrl = "";
  @state() private _prodPrDraft = false;
  @state() private _useAuth = false;
  @state() private _authClientId = "";
  @state() private _authAuthorityUrl = PREPROD_AUTHORITY_URL;
  @state() private _authScopes = "";
  @state() private _stageDiffers = false;
  @state() private _stageClientId = "";
  @state() private _prodClientId = "";
  @state() private _prodAuthorityUrl = PROD_AUTHORITY_URL;

  private get _remoteName(): string {
    return this._mfeName ? kebabToCamel(this._mfeName) : "";
  }

  private get _effectiveUrlPath(): string {
    return this._urlPathEdited ? this._urlPath : this._mfeName;
  }

  private get _urlPathValid(): boolean {
    return URL_PATH_PATTERN.test(this._effectiveUrlPath.trim());
  }

  private get _isValid(): boolean {
    if (!/^[a-z][a-z0-9-]*[a-z0-9]$/.test(this._mfeName)) return false;
    if (!this._urlPathValid) return false;
    if (!this._useAuth) return true;
    // The prod client ID stays optional; everything else the API requires must be present.
    if (!this._authClientId.trim() || !this._authAuthorityUrl.trim()) return false;
    if (this._stageDiffers && !this._stageClientId.trim()) return false;
    return Boolean(this._prodAuthorityUrl.trim());
  }

  private get _prodReusesPreprodClient(): boolean {
    if (!this._useAuth || !this._prodClientId.trim()) return false;
    const preprod = [this._authClientId.trim(), this._stageDiffers ? this._stageClientId.trim() : ""];
    return preprod.filter(Boolean).includes(this._prodClientId.trim());
  }

  private _authFor(clientId: string, authorityUrl: string): AuthConfig | undefined {
    if (!this._useAuth) return undefined;
    return {
      clientId,
      authorityUrl,
      ...(this._authScopes.trim() ? { scopes: this._authScopes.trim() } : {}),
    };
  }

  private _entryFor(auth: AuthConfig | undefined): object {
    const extras: Record<string, unknown> = {
      moduleFederation: { remote: this._remoteName, module: "App" },
      product: { urlPath: this._effectiveUrlPath },
    };
    if (auth) extras.auth = auth;
    return { url: `/assets/${this._mfeName}/0/mf-manifest.json`, extras };
  }

  private get _manifestEntry(): object {
    return { [this._mfeName]: [this._entryFor(this._authFor(this._authClientId.trim(), this._authAuthorityUrl.trim()))] };
  }

  private _close() {
    this._mfeName = "";
    this._urlPath = "";
    this._urlPathEdited = false;
    this._error = "";
    this._prUrl = "";
    this._prodPrUrl = "";
    this._prodPrDraft = false;
    this._applying = false;
    this._useAuth = false;
    this._authClientId = "";
    this._authAuthorityUrl = PREPROD_AUTHORITY_URL;
    this._authScopes = "";
    this._stageDiffers = false;
    this._stageClientId = "";
    this._prodClientId = "";
    this._prodAuthorityUrl = PROD_AUTHORITY_URL;
    this.dispatchEvent(new CustomEvent("close"));
  }

  private _onNameInput(e: Event) {
    this._mfeName = (e.target as HTMLInputElement).value.trim();
  }

  private _onUrlPathInput(e: Event) {
    this._urlPath = (e.target as HTMLInputElement).value.trim();
    this._urlPathEdited = true;
  }

  private _onUseAuthChange(e: Event) {
    this._useAuth = (e.target as HTMLInputElement).checked;
  }

  private _onAuthClientIdInput(e: Event) {
    this._authClientId = (e.target as HTMLInputElement).value.trim();
  }

  private _onAuthorityUrlInput(e: Event) {
    this._authAuthorityUrl = (e.target as HTMLInputElement).value.trim();
  }

  private _onScopesInput(e: Event) {
    this._authScopes = (e.target as HTMLInputElement).value.trim();
  }

  private _onStageDiffersChange(e: Event) {
    this._stageDiffers = (e.target as HTMLInputElement).checked;
  }

  private _onStageClientIdInput(e: Event) {
    this._stageClientId = (e.target as HTMLInputElement).value.trim();
  }

  private _onProdClientIdInput(e: Event) {
    this._prodClientId = (e.target as HTMLInputElement).value.trim();
  }

  private _onProdAuthorityUrlInput(e: Event) {
    this._prodAuthorityUrl = (e.target as HTMLInputElement).value.trim();
  }

  private async _submit() {
    if (!this._isValid || this._applying || this._prodReusesPreprodClient) return;

    this._applying = true;
    this._error = "";

    const auth = this._authFor(this._authClientId.trim(), this._authAuthorityUrl.trim());

    try {
      const result = await api.registerMfe({
        mfeName: this._mfeName,
        remoteName: this._remoteName,
        module: "App",
        urlPath: this._effectiveUrlPath,
        ...(auth ? { auth } : {}),
        ...(auth && this._stageDiffers
          ? { stageAuth: this._authFor(this._stageClientId.trim(), this._authAuthorityUrl.trim()) }
          : {}),
        ...(auth
          ? { prodAuth: this._authFor(this._prodClientId.trim(), this._prodAuthorityUrl.trim()) }
          : {}),
      });
      this._prUrl = result.prUrl;
      this._prodPrUrl = result.prodPrUrl;
      this._prodPrDraft = result.prodPrDraft;
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Registration failed";
    } finally {
      this._applying = false;
    }
  }

  render() {
    if (!this.open) return nothing;

    if (this._prUrl) {
      return html`
        <bee-modal open in-root title="Product Registered" @bee-request-close=${this._close}>
          <div class="success-message">
            <p>Product <strong>${this._mfeName}</strong> registered in two stacked PRs.</p>
            <p><a href=${this._prUrl} target="_blank" rel="noopener noreferrer">Preprod PR</a> — merge this first.</p>
            <p>
              <a href=${this._prodPrUrl} target="_blank" rel="noopener noreferrer">Prod PR</a>
              ${this._prodPrDraft ? " — draft, needs a prod client ID." : " — merge when ready for prod."}
            </p>
          </div>
          <div slot="footer" class="modal-footer">
            <bee-button label="Close" bee-type="secondary-grey" @click=${this._close}></bee-button>
          </div>
        </bee-modal>
      `;
    }

    return html`
      <bee-modal open in-root title="Register New Product" @bee-request-close=${this._close}>
        <bee-alert variant="info" open>
          <span slot="message">Before registering, create the MFE repository via the <a href="https://portal.accuris.dev/create/templates/default/github-software-component-v2" target="_blank" rel="noopener noreferrer">Backstage scaffolder</a> and select <strong>HAWAII Product MFE</strong> as the component language.</span>
        </bee-alert>

        <div class="form-field">
          <label>Product Name</label>
          <bee-input
            placeholder="e.g., connect-viewer"
            .value=${this._mfeName}
            @bee-input=${this._onNameInput}
          ></bee-input>
          <div class="help">The <code>name</code> field from the MFE's <code>package.json</code>. Must be lowercase with hyphens.</div>
        </div>

        ${this._mfeName ? html`
          <div class="form-field">
            <label>URL Path</label>
            <bee-input
              .value=${this._effectiveUrlPath}
              @bee-input=${this._onUrlPathInput}
            ></bee-input>
            <div class="help">Defaults to the product name. Change it when the route differs — <code>udvp</code> registers <code>view</code>. One segment, no slashes.</div>
            ${this._effectiveUrlPath.trim() && !this._urlPathValid ? html`
              <div class="field-error">Must be a single lowercase segment, e.g. <code>quote-admin</code> — no slashes.</div>
            ` : nothing}
          </div>

          <div class="derived-fields">
            <div class="field-row">
              <span class="field-label">Remote:</span>
              <span class="field-value">${this._remoteName}</span>
            </div>
            <div class="field-row">
              <span class="field-label">Module:</span>
              <span class="field-value">App</span>
            </div>
            <div class="field-row">
              <span class="field-label">Asset URL:</span>
              <span class="field-value">/assets/${this._mfeName}/0/mf-manifest.json</span>
            </div>
          </div>

          <div class="form-field">
            <label class="checkbox-row">
              <input type="checkbox" .checked=${this._useAuth} @change=${this._onUseAuthChange} />
              <span>This product uses OAuth</span>
            </label>
            <div class="help">Adds an <code>auth</code> block. Leave off and the preprod and prod entries are identical.</div>
          </div>

          ${this._useAuth ? html`
            <div class="auth-section">
              <div class="section-title">Preprod <span>dev and stage</span></div>

              <div class="form-field">
                <label>Client ID</label>
                <bee-input
                  placeholder="e.g., connect-viewer-preprod"
                  .value=${this._authClientId}
                  @bee-input=${this._onAuthClientIdInput}
                ></bee-input>
              </div>

              <div class="form-field">
                <label>Authority URL</label>
                <bee-input
                  .value=${this._authAuthorityUrl}
                  @bee-input=${this._onAuthorityUrlInput}
                ></bee-input>
                <div class="help">Prefilled with the preprod default.</div>
              </div>

              <div class="form-field">
                <label>Scopes (optional)</label>
                <bee-input
                  placeholder="e.g., openid"
                  .value=${this._authScopes}
                  @bee-input=${this._onScopesInput}
                ></bee-input>
              </div>

              <div class="form-field">
                <label class="checkbox-row">
                  <input type="checkbox" .checked=${this._stageDiffers} @change=${this._onStageDiffersChange} />
                  <span>Stage uses a different client ID</span>
                </label>
              </div>

              ${this._stageDiffers ? html`
                <div class="form-field">
                  <label>Stage Client ID</label>
                  <bee-input
                    .value=${this._stageClientId}
                    @bee-input=${this._onStageClientIdInput}
                  ></bee-input>
                </div>
              ` : nothing}

              <div class="section-title">Prod <span>second PR</span></div>

              <div class="form-field">
                <label>Client ID (optional)</label>
                <bee-input
                  placeholder="e.g., connect-viewer-prod"
                  .value=${this._prodClientId}
                  @bee-input=${this._onProdClientIdInput}
                ></bee-input>
                <div class="help">Leave blank if you don't have it yet — the prod PR opens as a draft.</div>
                ${this._prodReusesPreprodClient ? html`
                  <div class="field-error">Prod must not reuse the preprod client ID.</div>
                ` : nothing}
              </div>

              <div class="form-field">
                <label>Authority URL</label>
                <bee-input
                  .value=${this._prodAuthorityUrl}
                  @bee-input=${this._onProdAuthorityUrlInput}
                ></bee-input>
                <div class="help">Prefilled with the prod default.</div>
              </div>
            </div>
          ` : nothing}

          <div class="json-preview">
            <details open>
              <summary>Manifest entry preview</summary>
              <pre>${JSON.stringify(this._manifestEntry, null, 2)}</pre>
            </details>
          </div>
          <div class="form-field">
            <div class="help">Creates two stacked PRs: <strong>dev + stage</strong> onto <code>main</code>, then <strong>prod</strong> onto that branch.</div>
          </div>
        ` : nothing}

        ${this._error ? html`<bee-alert variant="error" open>
          <span slot="message">${this._error}</span>
        </bee-alert>` : nothing}

        <div slot="footer" class="modal-footer">
          <bee-button label="Cancel" bee-type="secondary-grey" @click=${this._close} ?disabled=${this._applying}></bee-button>
          <bee-button
            label=${this._applying ? "Creating PRs..." : "Register product"}
            bee-type="primary"
            @click=${this._submit}
            ?disabled=${!this._isValid || this._applying || this._prodReusesPreprodClient}
            ?loading=${this._applying}
          ></bee-button>
        </div>
      </bee-modal>
    `;
  }
}
